import { Trans, useTranslation } from "react-i18next"
import {
  Text,
  Group,
  Badge,
  Paper,
  SimpleGrid,
  Timeline,
  ThemeIcon,
  Table,
  Box,
} from "@mantine/core"
import { STATE_MAP } from "../models/travelersCheck"
import type { TravelersCheckDetail } from "../models/travelersCheck"

function EmptyValue() {
  return (
    <Text size="sm" fs="italic" c="gray.5">
      Não preenchido
    </Text>
  )
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" style={{ minWidth: 110, flexShrink: 0 }}>
        {label}
      </Text>
      <Box style={{ flex: 1 }}>{children}</Box>
    </Group>
  )
}

interface Props {
  check: TravelersCheckDetail
}

export default function TravelersCheckDetailContent({ check }: Props) {
  const { t } = useTranslation()

  return (
    <>
      <Paper withBorder p="md" mb="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <MetaRow label={t("travelersChecks.issuingAgency")}>
            <Text fw={500}>{check.issuing_agency_name}</Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.redeemAgency")}>
            <Text fw={500}>{check.redeem_agency_name || <EmptyValue />}</Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.claimAgency")}>
            <Text fw={500}>{check.claim_agency_name || <EmptyValue />}</Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.state")}>
            <Badge color={STATE_MAP[check.state]?.color ?? "gray"} variant="light" size="sm">
              {t(`travelersChecks.${check.state}`)}
            </Badge>
          </MetaRow>
          <MetaRow label={t("travelersChecks.clientName")}>
            <Text fw={500}>{check.client_name || <EmptyValue />}</Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.issuingFile")}>
            <Text fw={500}>{check.issuing_file?.toString() ?? <EmptyValue />}</Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.redeemFile")}>
            <Text fw={500}>{check.redeem_file?.toString() ?? <EmptyValue />}</Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.value")}>
            <Text fw={500}>{check.value != null ? `${check.value.toFixed(2)} €` : <EmptyValue />}</Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.valueInWords")}>
            <Text fw={500}>{check.value_in_words || <EmptyValue />}</Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.validUntil")}>
            <Text fw={500}>
              {check.valid_until ? new Date(check.valid_until).toLocaleDateString("pt-PT") : <EmptyValue />}
            </Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.issuedAt")}>
            <Text fw={500}>{check.issued_at || <EmptyValue />}</Text>
          </MetaRow>
          <MetaRow label={t("travelersChecks.redeemAt")}>
            <Text fw={500}>{check.redeem_at || <EmptyValue />}</Text>
          </MetaRow>
        </SimpleGrid>
      </Paper>

      {check.logs && check.logs.length > 0 && (
        <Paper withBorder p="md" mb="md">
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts="0.05em" mb="md">
            <Trans i18nKey="travelersChecks.history" />
          </Text>
          <Timeline active={check.logs.length - 1} bulletSize={24} lineWidth={2}>
            {check.logs.map((log) => (
              <Timeline.Item
                key={log.id}
                bullet={
                  <ThemeIcon
                    size={22}
                    variant="filled"
                    color={STATE_MAP[log.state_next]?.color ?? "gray"}
                    radius="xl"
                  >
                    <i className="fa fa-history" style={{ fontSize: 12 }} />
                  </ThemeIcon>
                }
                title={
                  <Group gap="xs">
                    <Text fw={500} size="sm">{log.agency_name}</Text>
                    <Badge color={STATE_MAP[log.state_next]?.color ?? "gray"} variant="light" size="sm">
                      {t(`travelersChecks.${log.state_next}`)}
                    </Badge>
                  </Group>
                }
              >
                <Text size="xs" c="dimmed" mb="xs">
                  {log.user_name} — {log.created_at ? new Date(log.created_at).toLocaleString("pt-PT") : ""}
                </Text>
                {log.values && log.values.length > 0 && (
                  <Table withTableBorder withColumnBorders mb="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Campo</Table.Th>
                        <Table.Th>Valor anterior</Table.Th>
                        <Table.Th>Valor atualizado</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {log.values.map((v, i) => (
                        <Table.Tr key={i}>
                          <Table.Td>{v.attribute}</Table.Td>
                          <Table.Td>{v.old}</Table.Td>
                          <Table.Td>{v.new}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        </Paper>
      )}

    </>
  )
}
